import React, { useState, useEffect } from "react";
import Hamburger from "../components/Hamburger";
const API_URL = process.env.REACT_APP_API_URL;

export default function Manage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [selectedReservations, setSelectedReservations] = useState(new Set());
  const [loadingReservations, setLoadingReservations] = useState(false);

  // Fetch all reservations when component mounts
  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    setLoadingReservations(true);
    try {
      const res = await fetch(`${API_URL}/api/reservations`);
      if (res.ok) {
        const data = await res.json();
        console.log('API Response:', data); // Debug log
        setReservations(data.reservations || data); // Handle both formats
      } else {
        setResult("Failed to fetch reservations.");
      }
    } catch (err) {
      console.log('Fetch error:', err); // Debug log
      setResult("Error fetching reservations.");
    }
    setLoadingReservations(false);
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("Are you sure you want to delete ALL reservations?")) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/reservations`, {
        method: "DELETE",
      });
      if (res.ok) {
        setResult("All reservations deleted successfully.");
        setReservations([]);
        setSelectedReservations(new Set());
      } else {
        setResult("Failed to delete reservations.");
      }
    } catch (err) {
      setResult("Error deleting reservations.");
    }
    setLoading(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedReservations.size === 0) {
      setResult("Please select at least one reservation to delete.");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedReservations.size} selected reservation(s)?`)) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      const deletePromises = Array.from(selectedReservations).map(id => 
        fetch(`${API_URL}/api/reservations/${id}`, {
          method: "DELETE",
        })
      );
      
      const results = await Promise.all(deletePromises);
      const allSuccessful = results.every(res => res.ok);
      
      if (allSuccessful) {
        setResult(`${selectedReservations.size} reservation(s) deleted successfully.`);
        // Remove deleted reservations from the list
        setReservations(prev => prev.filter(res => !selectedReservations.has(res.id)));
        setSelectedReservations(new Set());
      } else {
        setResult("Some reservations failed to delete. Please try again.");
      }
    } catch (err) {
      setResult("Error deleting selected reservations.");
    }
    setLoading(false);
  };

  const handleSelectReservation = (id) => {
    const newSelected = new Set(selectedReservations);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedReservations(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedReservations.size === reservations.length) {
      setSelectedReservations(new Set());
    } else {
      setSelectedReservations(new Set(reservations.map(res => res.id)));
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="container py-4">
      <nav className="navbar">
        <Hamburger />
      </nav>
      
      <div className="row">
        <div className="col-12">
          <h2 className="mb-4 pt-3 text-center">Manage Reservations</h2>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            <button
              className="btn btn-danger btn-lg"
              onClick={handleDeleteAll}
              disabled={loading || loadingReservations}
            >
              <i className="bi bi-trash"></i>
              {loading ? "Deleting..." : "Delete All Reservations"}
            </button>
            
            <button
              className="btn btn-warning btn-lg"
              onClick={handleDeleteSelected}
              disabled={loading || loadingReservations || selectedReservations.size === 0}
            >
              <i className="bi bi-trash3"></i>
              {loading ? "Deleting..." : `Delete Selected (${selectedReservations.size})`}
            </button>
            
            <button
              className="btn btn-primary btn-lg"
              onClick={fetchReservations}
              disabled={loadingReservations}
            >
              <i className="bi bi-arrow-clockwise"></i>
              {loadingReservations ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* Result Message */}
      {result && (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-info text-center" role="alert">
              <strong>{result}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Debug Information */}
      <div className="row mb-3">
        <div className="col-12">
          <small className="text-muted">
            Debug: Found {reservations.length} reservations | API URL: {API_URL}
          </small>
        </div>
      </div>

      {/* Reservations Section */}
      <div className="row">
        <div className="col-12">
          {loadingReservations ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
                <span className="visually-hidden">Loading reservations...</span>
              </div>
              <p className="mt-3 text-muted">Loading reservations...</p>
            </div>
          ) : reservations.length > 0 ? (
            <div className="card shadow">
              <div className="card-header bg-primary text-white">
                <div className="row align-items-center">
                  <div className="col">
                    <h5 className="mb-0">
                      <i className="bi bi-calendar-check me-2"></i>
                      Reservations ({reservations.length})
                    </h5>
                  </div>
                  <div className="col-auto">
                    <div className="form-check form-check-reverse">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="selectAll"
                        checked={selectedReservations.size === reservations.length && reservations.length > 0}
                        onChange={handleSelectAll}
                      />
                      <label className="form-check-label text-white" htmlFor="selectAll">
                        Select All
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th width="60" className="text-center">Select</th>
                        <th><i className="bi bi-person me-1"></i>Name</th>
                        <th className="text-center">Reservation ID</th>
                        <th><i className="bi bi-envelope me-1"></i>Email</th>
                        <th><i className="bi bi-telephone me-1"></i>Phone</th>
                        <th className="text-center"><i className="bi bi-people me-1"></i>Attending?</th>
                        <th><i className="bi bi-calendar-event me-1"></i>Date & Time</th>
                        <th><i className="bi bi-clock me-1"></i>Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservations.map((reservation) => (
                        <tr 
                          key={reservation.id} 
                          className={selectedReservations.has(reservation.id) ? 'table-primary' : ''}
                        >
                          <td className="text-center">
                            <div className="form-check d-flex justify-content-center">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={selectedReservations.has(reservation.id)}
                                onChange={() => handleSelectReservation(reservation.id)}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="fw-bold text-dark">
                              {reservation.first_name} {reservation.last_name}
                            </div>
                          </td>
                          <td>
                            <div className="fw-bold text-dark text-center">
                              {reservation.reservationid}
                            </div>
                          </td>
                          <td>
                            <a href={`mailto:${reservation.email}`} className="text-decoration-none">
                              {reservation.email}
                            </a>
                          </td>
                          <td>
                            <a href={`tel:${reservation.phone}`} className="text-decoration-none">
                              {reservation.phone}
                            </a>
                          </td>
                          <td className="text-center">
                            <span className={`badge fs-6 ${reservation.attending ? "bg-success" : "bg-danger"}`}>
                              {reservation.attending ? "Yes" : "No"}
                            </span>
                          </td>
                          <td>
                            <div className="fw-semibold">{formatDateTime(reservation.date_created)}</div>
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatDateTime(reservation.last_updated)}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Summary Footer */}
              <div className="card-footer bg-light">
                <div className="row text-center text-muted">
                  <div className="col-md-4">
                    <strong>Total Reservations:</strong> {reservations.length}
                  </div>
                  <div className="col-md-4">
                    <strong>Selected:</strong> {selectedReservations.size}
                  </div>
                  <div className="col-md-4">
                    <strong>Total Guests:</strong> {reservations.reduce((sum, res) => sum + parseInt(res.party_size || 0), 0)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card shadow">
              <div className="card-body text-center py-5">
                <i className="bi bi-calendar-x display-1 text-muted mb-3"></i>
                <h5 className="text-muted">No reservations found</h5>
                <p className="text-muted">Reservations will appear here once they are made.</p>
                <button 
                  className="btn btn-outline-primary"
                  onClick={fetchReservations}
                  disabled={loadingReservations}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}